import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import StatusMessage from '../../components/admin/StatusMessage';
import ConfirmationModal from '../../components/admin/ConfirmationModal';

export default function DistributionListsPage() {
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateEmails, setSelectedCandidateEmails] = useState([]);
  const [recipientForm, setRecipientForm] = useState({ name: '', email: '', phone: '' });
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [listForm, setListForm] = useState('');
  const [editingListId, setEditingListId] = useState(null);
  const [showListForm, setShowListForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [fileImportLoading, setFileImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const selectedList = useMemo(
    () => lists.find((list) => list.id === selectedListId) || null,
    [lists, selectedListId]
  );

  const showMessage = (type, text) => setMessage({ type, text });

  const getError = (error) => error.response?.data?.detail || error.message || 'Request failed';

  const loadLists = async (preferredId) => {
    try {
      const response = await api.get('/api/admin/distribution-lists');
      const nextLists = response.data || [];
      setLists(nextLists);
      setSelectedListId((currentId) => {
        if (preferredId && nextLists.some((list) => list.id === preferredId)) return preferredId;
        if (currentId && nextLists.some((list) => list.id === currentId)) return currentId;
        return nextLists[0]?.id || '';
      });
    } catch (error) {
      showMessage('error', `Failed to load distribution lists: ${getError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    if (!selectedListId) return;
    setMembersLoading(true);
    try {
      const response = await api.get(`/api/admin/distribution-lists/${selectedListId}/members`, {
        params: { q: search.trim() || undefined },
      });
      setMembers(response.data || []);
    } catch (error) {
      showMessage('error', `Failed to load recipients: ${getError(error)}`);
    } finally {
      setMembersLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    setCandidates([]);
    setSelectedCandidateEmails([]);
    loadMembers();
  }, [selectedListId, search]);

  const submitList = async (event) => {
    event.preventDefault();
    if (!listForm.trim()) return;
    try {
      if (editingListId) {
        await api.patch(`/api/admin/distribution-lists/${editingListId}`, { name: listForm });
        showMessage('success', 'Distribution list renamed');
        await loadLists(editingListId);
      } else {
        const response = await api.post('/api/admin/distribution-lists', { name: listForm });
        showMessage('success', 'Distribution list created');
        await loadLists(response.data.id);
      }
      setListForm('');
      setEditingListId(null);
      setShowListForm(false);
    } catch (error) {
      showMessage('error', getError(error));
    }
  };

  const submitRecipient = async (event) => {
    event.preventDefault();
    if (!selectedList || selectedList.is_global) return;
    try {
      if (editingRecipient) {
        await api.patch(`/api/admin/distribution-lists/recipients/${editingRecipient.id}`, recipientForm);
        showMessage('success', 'Recipient updated');
      } else {
        await api.post(`/api/admin/distribution-lists/${selectedListId}/members`, recipientForm);
        showMessage('success', 'Recipient added');
      }
      setRecipientForm({ name: '', email: '', phone: '' });
      setEditingRecipient(null);
      await Promise.all([loadMembers(), loadLists(selectedListId)]);
    } catch (error) {
      showMessage('error', getError(error));
    }
  };

  const startRecipientEdit = (recipient) => {
    setEditingRecipient(recipient);
    setRecipientForm({ name: recipient.name, email: recipient.email, phone: recipient.phone || '' });
  };

  const cancelRecipientEdit = () => {
    setEditingRecipient(null);
    setRecipientForm({ name: '', email: '', phone: '' });
  };

  const executeConfirmation = async () => {
    if (!confirmation) return;
    const { action, target } = confirmation;
    try {
      if (action === 'delete-list') {
        await api.delete(`/api/admin/distribution-lists/${target.id}`);
        showMessage('success', 'Distribution list deleted');
        await loadLists();
      } else if (action === 'remove-member') {
        await api.delete(`/api/admin/distribution-lists/${selectedListId}/members/${target.id}`);
        showMessage('success', 'Recipient removed from this list');
        await Promise.all([loadMembers(), loadLists(selectedListId)]);
      } else if (action === 'delete-recipient') {
        await api.delete(`/api/admin/distribution-lists/recipients/${target.id}`);
        showMessage('success', 'Recipient deleted from every list');
        await Promise.all([loadMembers(), loadLists(selectedListId)]);
      } else if (action === 'import') {
        await importCandidates();
      }
    } catch (error) {
      showMessage('error', getError(error));
    } finally {
      setConfirmation(null);
    }
  };

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const response = await api.get('/api/admin/distribution-lists/import/events');
      setEvents(response.data || []);
    } catch (error) {
      showMessage('error', `Failed to load events: ${getError(error)}`);
    } finally {
      setEventsLoading(false);
    }
  };

  const toggleEvent = (eventId) => {
    setSelectedEventIds((current) => current.includes(eventId)
      ? current.filter((id) => id !== eventId)
      : [...current, eventId]);
  };

  const previewImport = async () => {
    if (!selectedEventIds.length) return;
    setImportLoading(true);
    try {
      const response = await api.post('/api/admin/distribution-lists/import/preview', {
        event_ids: selectedEventIds,
      });
      setCandidates(response.data.candidates || []);
      setSelectedCandidateEmails([]);
      showMessage('success', `${response.data.total || 0} unique registrants found`);
    } catch (error) {
      showMessage('error', `Failed to preview registrants: ${getError(error)}`);
    } finally {
      setImportLoading(false);
    }
  };

  const importCandidates = async () => {
    const selectedCandidates = candidates.filter((candidate) => selectedCandidateEmails.includes(candidate.email));
    setImportLoading(true);
    try {
      const response = await api.post(`/api/admin/distribution-lists/${selectedListId}/import`, {
        recipients: selectedCandidates.map((candidate) => ({
          name: candidate.name,
          email: candidate.email,
          phone: candidate.phone || null,
        })),
      });
      showMessage('success', `Import complete: ${response.data.added} added, ${response.data.updated} updated, ${response.data.already_present} already present`);
      setSelectedCandidateEmails([]);
      await Promise.all([loadMembers(), loadLists(selectedListId)]);
    } catch (error) {
      showMessage('error', `Import failed: ${getError(error)}`);
    } finally {
      setImportLoading(false);
    }
  };

  const importFileRecipients = async (event) => {
    event.preventDefault();
    if (!importFile || !selectedListId) return;
    setFileImportLoading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    try {
      const response = await api.post(`/api/admin/distribution-lists/${selectedListId}/import-file`, formData);
      showMessage('success', `File import complete: ${response.data.added} added, ${response.data.updated} updated, ${response.data.already_present} already present`);
      setImportFile(null);
      event.target.reset();
      await Promise.all([loadMembers(), loadLists(selectedListId)]);
    } catch (error) {
      showMessage('error', `File import failed: ${getError(error)}`);
    } finally {
      setFileImportLoading(false);
    }
  };

  const selectImportFile = (file) => {
    if (!file) return;
    const extension = file.name.toLowerCase().split('.').pop();
    if (!['csv', 'xlsx', 'xlsm'].includes(extension)) {
      showMessage('error', 'Please choose a CSV or Excel file');
      return;
    }
    setImportFile(file);
  };

  const toggleCandidate = (email) => {
    setSelectedCandidateEmails((current) => current.includes(email)
      ? current.filter((candidateEmail) => candidateEmail !== email)
      : [...current, email]);
  };

  const allCandidatesSelected = candidates.length > 0 && selectedCandidateEmails.length === candidates.length;
  const filteredEvents = events.filter((event) => event.event_name.toLowerCase().includes(eventSearch.trim().toLowerCase()));

  if (loading) {
    return <div className="admin-distribution-lists-page"><p className="empty-state">Loading distribution lists...</p></div>;
  }

  return (
    <section className="admin-distribution-lists-page">
      <AdminSectionHeader
        title="Manage DLs"
        subtitle="Create and manage distribution lists and their recipients"
      />

      {message.text && <StatusMessage type={message.type} message={message.text} onClose={() => setMessage({ type: '', text: '' })} />}

      <div className="dl-workspace">
        <aside className="dl-list-panel" aria-label="Distribution lists">
          <div className="dl-panel-heading">
            <h3>Lists</h3>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => { setShowListForm((current) => !current); setEditingListId(null); setListForm(''); }}
              title="Create distribution list"
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
              <span className="sr-only">Create list</span>
            </button>
          </div>

          {showListForm && (
            <form className="dl-list-form" onSubmit={submitList}>
              <label htmlFor="new-list-name">{editingListId ? 'Rename list' : 'New list name'}</label>
              <input id="new-list-name" value={listForm} onChange={(event) => setListForm(event.target.value)} required autoFocus />
              <div className="dl-form-actions">
                <button type="submit" className="btn btn-sm">{editingListId ? 'Save' : 'Create'}</button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setShowListForm(false); setEditingListId(null); }}>Cancel</button>
              </div>
            </form>
          )}

          <div className="dl-list-items">
                        {lists.map((list) => (
              <div key={list.id} className={`dl-list-item${selectedListId === list.id ? ' is-selected' : ''}`}>
                <button type="button" className="dl-list-select" onClick={() => setSelectedListId(list.id)} aria-pressed={selectedListId === list.id}>
                  <span className="dl-list-name">{list.name}</span>
                  <span className="dl-list-count">{list.member_count}</span>
                </button>
                {!list.is_global && (
                  <div className="dl-list-actions">
                    <button type="button" className="icon-button" onClick={() => { setEditingListId(list.id); setListForm(list.name); setShowListForm(true); }} title={list.is_system ? 'Immutable System managed DLs' : `Rename ${list.name}`} disabled={list.is_system}>
                      <i className="fa-solid fa-pen" aria-hidden="true" />
                    </button>
                    <button type="button" className="icon-button danger" onClick={() => setConfirmation({ action: 'delete-list', target: list })} title={list.is_system ? 'Immutable System managed DLs' : `Delete ${list.name}`} disabled={list.is_system}>
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <div className="dl-main-panel">
          {selectedList && (
            <>
              <div className="dl-selected-header">
                <div>
                  <h3>{selectedList.name}</h3>
                  <p>{selectedList.member_count} recipient{selectedList.member_count === 1 ? '' : 's'}</p>
                </div>
                {selectedList.is_global && <span className="dl-readonly-badge"><i className="fa-solid fa-lock" aria-hidden="true" /> Read-only</span>}
              </div>

              {selectedList.is_global && <div className="dl-notice">Global membership is derived from every regular distribution list. Add recipients to a regular list to include them here.</div>}

              {!selectedList.is_global && (
                <form className="dl-recipient-form" onSubmit={submitRecipient}>
                  <div className="dl-form-heading">
                    <h4>{editingRecipient ? 'Edit recipient' : 'Add recipient'}</h4>
                    {editingRecipient && <button type="button" className="btn btn-sm btn-secondary" onClick={cancelRecipientEdit}>Cancel edit</button>}
                  </div>
                  <div className="dl-form-grid">
                    <label>Name<input value={recipientForm.name} onChange={(event) => setRecipientForm({ ...recipientForm, name: event.target.value })} required /></label>
                    <label>Email<input type="email" value={recipientForm.email} onChange={(event) => setRecipientForm({ ...recipientForm, email: event.target.value })} required /></label>
                    <label>Phone <span className="optional-label">optional</span><input type="tel" value={recipientForm.phone} onChange={(event) => setRecipientForm({ ...recipientForm, phone: event.target.value })} /></label>
                  </div>
                  <button type="submit" className="btn"><i className="fa-solid fa-save" aria-hidden="true" /> {editingRecipient ? 'Save recipient' : 'Add recipient'}</button>
                </form>
              )}

              <details className="dl-members-section">
                <summary className="dl-members-summary">
                  <span><i className="fa-solid fa-users" aria-hidden="true" /> DL members</span>
                  <span className="dl-list-count">{selectedList.member_count}</span>
                </summary>
                <div className="dl-members-content">
                  <div className="dl-members-toolbar">
                    <label htmlFor="recipient-search" className="sr-only">Search recipients</label>
                    <input id="recipient-search" type="search" placeholder="Search name, email, or phone" value={search} onChange={(event) => setSearch(event.target.value)} />
                    <button type="button" className="btn btn-secondary" onClick={loadMembers} title="Refresh recipients"><i className="fa-solid fa-rotate" aria-hidden="true" /> Refresh</button>
                  </div>
                  {membersLoading ? <p className="empty-state">Loading recipients...</p> : members.length === 0 ? <p className="empty-state">No recipients found.</p> : (
                    <div className="dl-member-list">
                      {members.map((member) => (
                        <article key={member.id} className="dl-member-row">
                          <div className="dl-member-details">
                            <strong>{member.name}</strong>
                            <span>{member.email}</span>
                            {member.phone && <span>{member.phone}</span>}
                          </div>
                          <div className="dl-member-actions">
                            {!selectedList.is_global && <button type="button" className="icon-button" onClick={() => startRecipientEdit(member)} title={`Edit ${member.name}`}><i className="fa-solid fa-pen" aria-hidden="true" /></button>}
                            {!selectedList.is_global && <button type="button" className="icon-button" onClick={() => setConfirmation({ action: 'remove-member', target: member })} title={`Remove ${member.name} from this list`}><i className="fa-solid fa-user-minus" aria-hidden="true" /></button>}
                            <button type="button" className="icon-button danger" onClick={() => setConfirmation({ action: 'delete-recipient', target: member })} title={`Delete ${member.name} from every list`}><i className="fa-solid fa-trash" aria-hidden="true" /></button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </details>

              {!selectedList.is_global && (
                <section className="dl-import-section" aria-labelledby="registrant-import-title">
                  <div className="dl-form-heading">
                    <div><h4 id="registrant-import-title">Import event registrants</h4><p>Select events, review candidates, then choose who to add.</p></div>
                    {!events.length && <button type="button" className="btn btn-secondary" onClick={loadEvents} disabled={eventsLoading}>{eventsLoading ? 'Loading...' : 'Load events'}</button>}
                  </div>
                  {events.length > 0 && <>
                    <label htmlFor="event-search" className="sr-only">Search events</label>
                    <input id="event-search" className="dl-event-search" type="search" placeholder="Search events" value={eventSearch} onChange={(event) => setEventSearch(event.target.value)} />
                    <div className="dl-event-list">
                      {filteredEvents.map((event) => <label key={event.id} className="dl-check-row"><input type="checkbox" checked={selectedEventIds.includes(event.id)} onChange={() => toggleEvent(event.id)} /><span>{event.event_name}<small>{new Date(event.date_time).toLocaleString()}</small></span></label>)}
                      {!filteredEvents.length && <p className="empty-state">No matching events.</p>}
                    </div>
                    <div className="dl-import-actions"><button type="button" className="btn" onClick={previewImport} disabled={!selectedEventIds.length || importLoading}>{importLoading ? 'Reviewing...' : 'Review registrants'}</button><button type="button" className="btn btn-secondary" onClick={() => { setSelectedEventIds([]); setCandidates([]); setSelectedCandidateEmails([]); }}>Clear</button></div>
                  </>}
                  {candidates.length > 0 && <div className="dl-candidate-review">
                    <div className="dl-form-heading"><h4>{candidates.length} candidates</h4><span>{selectedCandidateEmails.length} selected</span></div>
                    <div className="dl-import-actions"><button type="button" className="btn btn-sm btn-secondary" onClick={() => setSelectedCandidateEmails(allCandidatesSelected ? [] : candidates.map((candidate) => candidate.email))}>{allCandidatesSelected ? 'Clear selection' : 'Select all'}</button><button type="button" className="btn btn-sm" disabled={!selectedCandidateEmails.length || importLoading} onClick={() => setConfirmation({ action: 'import', target: { count: selectedCandidateEmails.length } })}>Add selected</button></div>
                    <div className="dl-candidate-list">{candidates.map((candidate) => <label key={candidate.email} className="dl-candidate-row"><input type="checkbox" checked={selectedCandidateEmails.includes(candidate.email)} onChange={() => toggleCandidate(candidate.email)} /><span><strong>{candidate.name}</strong><span>{candidate.email}{candidate.phone ? ` · ${candidate.phone}` : ''}</span><small>{candidate.source_events.map((source) => source.event_name).join(', ')}</small></span></label>)}</div>
                  </div>}
                </section>
              )}

              {!selectedList.is_global && (
                <section className="dl-import-section" aria-labelledby="file-import-title">
                  <div className="dl-form-heading">
                    <div><h4 id="file-import-title">Import from file</h4><p>Use a CSV or Excel file with headers: Name, Email, Phone. Phone may be empty.</p></div>
                  </div>
                  <form className="dl-file-import-form" onSubmit={importFileRecipients}>
                    <label
                      className={`dl-file-dropzone${isFileDragActive ? ' is-drag-active' : ''}`}
                      htmlFor="dl-file-input"
                      onDragEnter={(event) => { event.preventDefault(); setIsFileDragActive(true); }}
                      onDragOver={(event) => event.preventDefault()}
                      onDragLeave={(event) => { event.preventDefault(); setIsFileDragActive(false); }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setIsFileDragActive(false);
                        selectImportFile(event.dataTransfer.files?.[0]);
                      }}
                    >
                      <input
                        id="dl-file-input"
                        type="file"
                        accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(event) => selectImportFile(event.target.files?.[0])}
                      />
                      <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />
                      <span>{importFile ? importFile.name : 'Drop a CSV or Excel file here, or click to browse'}</span>
                      <small>Required columns: Name, Email, Phone</small>
                    </label>
                    <button type="submit" className="btn" disabled={!importFile || fileImportLoading}>
                      <i className="fa-solid fa-upload" aria-hidden="true" /> {fileImportLoading ? 'Importing...' : 'Import file'}
                    </button>
                  </form>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(confirmation)}
        title={confirmation?.action === 'delete-list' ? 'Delete distribution list?' : confirmation?.action === 'delete-recipient' ? 'Delete recipient everywhere?' : confirmation?.action === 'import' ? 'Add selected registrants?' : 'Remove recipient from list?'}
        message={confirmation?.action === 'delete-list' ? `This deletes ${confirmation.target.name} and its memberships. Recipients will remain available in other lists.` : confirmation?.action === 'delete-recipient' ? `This removes ${confirmation.target.name} and their membership from every distribution list.` : confirmation?.action === 'import' ? `Add ${confirmation.target.count} selected registrants to ${selectedList?.name}?` : `Remove ${confirmation?.target?.name} from ${selectedList?.name}?`}
        onConfirm={executeConfirmation}
        onCancel={() => setConfirmation(null)}
      />
    </section>
  );
}
